import { useState, useEffect } from 'react';
import { 
  CheckCircle, Clock, AlertCircle, ChevronDown, 
  ExternalLink, RefreshCw, Filter, Download,
  MessageSquare, FileJson, FileSpreadsheet, BarChart3,
  Users, Globe, Plus, Shield, Inbox, Search, LogOut, Map,
  CalendarDays
} from 'lucide-react';
import axios from 'axios';
import { ProviderAdminPanel } from '../components/admin/ProviderAdminPanel';
import { GooglePlacesPanel } from '../components/admin/GooglePlacesPanel';
import { QuickAddPanel } from '../components/admin/QuickAddPanel';
import { ClaimsPanel } from '../components/admin/ClaimsPanel';
import { AdminInbox } from '../components/admin/AdminInbox';
import { ProviderSearch } from '../components/admin/ProviderSearch';
import { ProviderDetail } from '../components/admin/ProviderDetail';
import { ProviderVerticalSearch } from '../components/admin/ProviderVerticalSearch';
import JourneyDashboard from '../components/admin/JourneyDashboard';
import { EventControlPanel } from '../components/admin/EventControlPanel';
import { AdminAuthProvider, useAdminAuth } from '../contexts/AdminAuthContext';
import { AdminLogin } from '../components/admin/AdminLogin';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  open: { label: 'Aperto', color: 'bg-amber-100 text-amber-800', icon: Clock },
  in_progress: { label: 'In Corso', color: 'bg-blue-100 text-blue-800', icon: RefreshCw },
  resolved: { label: 'Risolto', color: 'bg-green-100 text-green-800', icon: CheckCircle },
};

const PRIORITY_CONFIG = {
  1: { label: 'Alta', color: 'text-red-600' },
  2: { label: 'Media', color: 'text-amber-600' },
  3: { label: 'Bassa', color: 'text-gray-500' },
};

const CATEGORY_LABELS = {
  event_verification: 'Verifica Evento',
  verify_entity: 'Verifica Entità',
  verify_event: 'Verifica Evento (nuovo)',
  availability_check: 'Verifica Disponibilità',
  price_quote: 'Preventivo',
  special_request: 'Richiesta Speciale',
  accessibility: 'Accessibilità',
};

export const AdminPage = () => {
  return (
    <AdminAuthProvider>
      <AdminPageContent />
    </AdminAuthProvider>
  );
};

const AdminPageContent = () => {
  const { isAuthenticated, isLoading: authLoading, logout, admin } = useAdminAuth();
  
  // Show login if not authenticated
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <AdminLogin />;
  }
  
  return <AdminDashboard logout={logout} adminUser={admin} />;
};

const AdminDashboard = ({ logout, adminUser }) => {
  const [helpRequests, setHelpRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Chat Export state
  const [chatStats, setChatStats] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [activeTab, setActiveTab] = useState('inbox'); // 'inbox' | 'search' | 'providers' | 'places' | 'quickadd' | 'claims' | 'requests' | 'chatexport'
  
  // Provider detail state
  const [selectedProviderId, setSelectedProviderId] = useState(null);
  const [searchInitialFilter, setSearchInitialFilter] = useState(null);

  useEffect(() => {
    loadHelpRequests();
    loadChatStats();
  }, [filter]);

  const loadHelpRequests = async () => {
    setIsLoading(true);
    try {
      const params = filter !== 'all' ? { status: filter } : {};
      const response = await axios.get(`${API}/admin/help-requests`, { params });
      setHelpRequests(response.data);
    } catch (error) {
      console.error('Failed to load help requests:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const loadChatStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/chat-export/stats`);
      setChatStats(response.data);
    } catch (error) {
      console.error('Failed to load chat stats:', error);
    }
  };
  
  const exportChats = async (format) => {
    setIsExporting(true);
    try {
      if (format === 'json') {
        const response = await axios.get(`${API}/admin/chat-export?format=json`);
        // Download as JSON file
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `chat_export_${new Date().toISOString().slice(0,10)}.json`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        // CSV - direct download
        window.location.href = `${API}/admin/chat-export?format=csv`;
      }
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export fallito. Riprova.');
    } finally {
      setIsExporting(false);
    }
  };

  const updateRequest = async (requestId, updates) => {
    setIsUpdating(true);
    try {
      await axios.patch(`${API}/admin/help-requests/${requestId}`, updates);
      await loadHelpRequests();
      setSelectedRequest(null);
      setResolutionNotes('');
    } catch (error) {
      console.error('Failed to update request:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const resolveRequest = () => {
    if (selectedRequest) {
      updateRequest(selectedRequest.id, {
        status: 'resolved',
        resolution_notes: resolutionNotes || 'Verificato e confermato'
      });
    }
  };

  const openCounts = {
    all: helpRequests.length,
    open: helpRequests.filter(r => r.status === 'open').length,
    in_progress: helpRequests.filter(r => r.status === 'in_progress').length,
    resolved: helpRequests.filter(r => r.status === 'resolved').length,
  };

  return (
    <div className="min-h-screen bg-umbria-paper" data-testid="admin-page">
      {/* Header */}
      <header className="bg-white border-b border-umbria-stone">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-serif text-2xl text-umbria-charcoal font-semibold">
                Feel Umbria Admin
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Gestione richieste e debug conversazioni
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500">
                {adminUser?.username || 'Admin'}
              </span>
              <button
                onClick={() => { loadHelpRequests(); loadChatStats(); }}
                className="btn-ghost flex items-center gap-2"
                data-testid="refresh-btn"
              >
                <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
                Aggiorna
              </button>
              <button
                onClick={logout}
                className="btn-ghost flex items-center gap-2 text-red-600 hover:bg-red-50"
                data-testid="logout-btn"
              >
                <LogOut size={16} />
                Esci
              </button>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-1 mt-4 border-b border-umbria-stone/30 overflow-x-auto pb-px">
            <button
              onClick={() => setActiveTab('inbox')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                activeTab === 'inbox'
                  ? 'bg-umbria-olive/10 text-umbria-olive border-b-2 border-umbria-olive'
                  : 'text-gray-500 hover:text-umbria-charcoal'
              }`}
              data-testid="tab-inbox"
            >
              <div className="flex items-center gap-2">
                <Inbox size={16} />
                Inbox
              </div>
            </button>
            <button
              onClick={() => setActiveTab('search')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                activeTab === 'search'
                  ? 'bg-umbria-olive/10 text-umbria-olive border-b-2 border-umbria-olive'
                  : 'text-gray-500 hover:text-umbria-charcoal'
              }`}
              data-testid="tab-search"
            >
              <div className="flex items-center gap-2">
                <Search size={16} />
                Cerca Provider
              </div>
            </button>
            <button
              onClick={() => setActiveTab('providers')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                activeTab === 'providers'
                  ? 'bg-umbria-olive/10 text-umbria-olive border-b-2 border-umbria-olive'
                  : 'text-gray-500 hover:text-umbria-charcoal'
              }`}
              data-testid="tab-providers"
            >
              <div className="flex items-center gap-2">
                <Users size={16} />
                Lista Fornitori
              </div>
            </button>
            <button
              onClick={() => setActiveTab('providers-vertical')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                activeTab === 'providers-vertical'
                  ? 'bg-umbria-olive/10 text-umbria-olive border-b-2 border-umbria-olive'
                  : 'text-gray-500 hover:text-umbria-charcoal'
              }`}
              data-testid="tab-providers-vertical"
            >
              <div className="flex items-center gap-2">
                <Shield size={16} />
                Verticali
              </div>
            </button>
            <button
              onClick={() => setActiveTab('places')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                activeTab === 'places'
                  ? 'bg-umbria-olive/10 text-umbria-olive border-b-2 border-umbria-olive'
                  : 'text-gray-500 hover:text-umbria-charcoal'
              }`}
              data-testid="tab-places"
            >
              <div className="flex items-center gap-2">
                <Globe size={16} />
                Google Places
              </div>
            </button>
            <button
              onClick={() => setActiveTab('quickadd')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                activeTab === 'quickadd'
                  ? 'bg-umbria-olive/10 text-umbria-olive border-b-2 border-umbria-olive'
                  : 'text-gray-500 hover:text-umbria-charcoal'
              }`}
              data-testid="tab-quickadd"
            >
              <div className="flex items-center gap-2">
                <Plus size={16} />
                Quick Add
              </div>
            </button>
            <button
              onClick={() => setActiveTab('claims')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                activeTab === 'claims'
                  ? 'bg-umbria-olive/10 text-umbria-olive border-b-2 border-umbria-olive'
                  : 'text-gray-500 hover:text-umbria-charcoal'
              }`}
              data-testid="tab-claims"
            >
              <div className="flex items-center gap-2">
                <Shield size={16} />
                Claims
              </div>
            </button>
            <button
              onClick={() => setActiveTab('journeys')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                activeTab === 'journeys'
                  ? 'bg-umbria-olive/10 text-umbria-olive border-b-2 border-umbria-olive'
                  : 'text-gray-500 hover:text-umbria-charcoal'
              }`}
              data-testid="tab-journeys"
            >
              <div className="flex items-center gap-2">
                <Map size={16} />
                Journey Ops
              </div>
            </button>
            <button
              onClick={() => setActiveTab('events')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                activeTab === 'events'
                  ? 'bg-umbria-olive/10 text-umbria-olive border-b-2 border-umbria-olive'
                  : 'text-gray-500 hover:text-umbria-charcoal'
              }`}
              data-testid="tab-events"
            >
              <div className="flex items-center gap-2">
                <CalendarDays size={16} />
                Eventi
              </div>
            </button>
            <button
              onClick={() => setActiveTab('requests')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                activeTab === 'requests'
                  ? 'bg-umbria-olive/10 text-umbria-olive border-b-2 border-umbria-olive'
                  : 'text-gray-500 hover:text-umbria-charcoal'
              }`}
              data-testid="tab-requests"
            >
              <div className="flex items-center gap-2">
                <Clock size={16} />
                Coda Verifiche
                {openCounts.open > 0 && (
                  <span className="bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full">
                    {openCounts.open}
                  </span>
                )}
              </div>
            </button>
            <button
              onClick={() => setActiveTab('chatexport')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                activeTab === 'chatexport'
                  ? 'bg-umbria-olive/10 text-umbria-olive border-b-2 border-umbria-olive'
                  : 'text-gray-500 hover:text-umbria-charcoal'
              }`}
              data-testid="tab-chatexport"
            >
              <div className="flex items-center gap-2">
                <MessageSquare size={16} />
                Export Chat
              </div>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        
        {/* ========== INBOX TAB ========== */}
        {activeTab === 'inbox' && (
          <AdminInbox 
            onSelectProvider={(id) => {
              setSelectedProviderId(id);
            }}
            onFilterChange={(filter) => {
              setSearchInitialFilter(filter);
              setActiveTab('search');
            }}
          />
        )}
        
        {/* ========== SEARCH TAB ========== */}
        {activeTab === 'search' && (
          <ProviderSearch 
            onSelectProvider={(id) => {
              setSelectedProviderId(id);
            }}
            initialFilter={searchInitialFilter}
          />
        )}
        
        {/* ========== PROVIDERS TAB (Legacy) ========== */}
        {activeTab === 'providers' && (
          <ProviderAdminPanel />
        )}
        
        {/* ========== PROVIDERS VERTICAL TAB ========== */}
        {activeTab === 'providers-vertical' && (
          <ProviderVerticalSearch 
            onSelectProvider={(provider) => {
              // TODO: Open provider detail with vertical context
              console.log('Selected provider:', provider);
            }}
          />
        )}
        
        {/* ========== GOOGLE PLACES TAB ========== */}
        {activeTab === 'places' && (
          <GooglePlacesPanel />
        )}
        
        {/* ========== QUICK ADD TAB ========== */}
        {activeTab === 'quickadd' && (
          <QuickAddPanel />
        )}
        
        {/* ========== CLAIMS TAB ========== */}
        {activeTab === 'claims' && (
          <ClaimsPanel />
        )}
        
        {/* ========== JOURNEY OPERATIONS DASHBOARD ========== */}
        {activeTab === 'journeys' && (
          <JourneyDashboard />
        )}
        
        {/* ========== EVENT CONTROL PANEL ========== */}
        {activeTab === 'events' && (
          <EventControlPanel />
        )}
        
        {/* ========== CHAT EXPORT TAB ========== */}
        {activeTab === 'chatexport' && (
          <div className="space-y-6" data-testid="chat-export-section">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl p-5 border border-umbria-stone/50 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <MessageSquare size={20} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-semibold text-umbria-charcoal">
                      {chatStats?.total_threads || 0}
                    </p>
                    <p className="text-sm text-gray-500">Conversazioni totali</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl p-5 border border-umbria-stone/50 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <BarChart3 size={20} className="text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-semibold text-umbria-charcoal">
                      {chatStats?.total_messages || 0}
                    </p>
                    <p className="text-sm text-gray-500">Messaggi totali</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl p-5 border border-umbria-stone/50 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <AlertCircle size={20} className="text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-semibold text-umbria-charcoal">
                      {openCounts.open}
                    </p>
                    <p className="text-sm text-gray-500">Help requests aperte</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Export Actions */}
            <div className="bg-white rounded-xl p-6 border border-umbria-stone/50 shadow-sm">
              <h3 className="font-serif text-lg font-semibold text-umbria-charcoal mb-2">
                Esporta Conversazioni
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                Scarica tutte le conversazioni per analizzare i flussi di Chiara. 
                Include messaggi, stato briefing, intent signals e help requests.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => exportChats('csv')}
                  disabled={isExporting}
                  className="flex items-center gap-2 px-5 py-3 bg-umbria-olive text-white rounded-xl font-medium hover:bg-umbria-olive/90 transition-colors disabled:opacity-50"
                  data-testid="export-csv-btn"
                >
                  <FileSpreadsheet size={18} />
                  {isExporting ? 'Esportazione...' : 'Scarica CSV'}
                </button>
                
                <button
                  onClick={() => exportChats('json')}
                  disabled={isExporting}
                  className="flex items-center gap-2 px-5 py-3 bg-umbria-charcoal text-white rounded-xl font-medium hover:bg-umbria-charcoal/90 transition-colors disabled:opacity-50"
                  data-testid="export-json-btn"
                >
                  <FileJson size={18} />
                  {isExporting ? 'Esportazione...' : 'Scarica JSON'}
                </button>
              </div>
              
              <div className="mt-6 p-4 bg-umbria-stone/20 rounded-lg">
                <h4 className="text-sm font-medium text-umbria-charcoal mb-2">Formato CSV</h4>
                <p className="text-xs text-gray-600">
                  Una riga per messaggio. Ideale per Excel/Google Sheets. Include: thread_id, ruolo, contenuto, timestamp, stato briefing, intent signals.
                </p>
                
                <h4 className="text-sm font-medium text-umbria-charcoal mt-4 mb-2">Formato JSON</h4>
                <p className="text-xs text-gray-600">
                  Struttura gerarchica completa. Ideale per analisi programmatica. Include tutti i dati del briefing e help requests per ogni thread.
                </p>
              </div>
            </div>
            
            {/* Recent Threads Preview */}
            {chatStats?.recent_threads?.length > 0 && (
              <div className="bg-white rounded-xl p-6 border border-umbria-stone/50 shadow-sm">
                <h3 className="font-serif text-lg font-semibold text-umbria-charcoal mb-4">
                  Conversazioni Recenti
                </h3>
                <div className="space-y-2">
                  {chatStats.recent_threads.slice(0, 5).map((thread, idx) => (
                    <div 
                      key={thread.id || idx}
                      className="flex items-center justify-between p-3 bg-umbria-stone/10 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-mono text-gray-500">
                          {thread.id?.slice(0, 8)}...
                        </span>
                        <span className="text-xs bg-umbria-olive/10 text-umbria-olive px-2 py-0.5 rounded">
                          {thread.locale?.toUpperCase() || 'IT'}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {thread.last_active_at ? new Date(thread.last_active_at).toLocaleString('it-IT') : '-'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Top Threads by Messages */}
            {chatStats?.top_threads_by_messages?.length > 0 && (
              <div className="bg-white rounded-xl p-6 border border-umbria-stone/50 shadow-sm">
                <h3 className="font-serif text-lg font-semibold text-umbria-charcoal mb-4">
                  Thread con più messaggi
                </h3>
                <div className="space-y-2">
                  {chatStats.top_threads_by_messages.map((item, idx) => (
                    <div 
                      key={item._id || idx}
                      className="flex items-center justify-between p-3 bg-umbria-stone/10 rounded-lg"
                    >
                      <span className="text-xs font-mono text-gray-500">
                        {item._id?.slice(0, 12)}...
                      </span>
                      <span className="text-sm font-medium text-umbria-charcoal">
                        {item.count} messaggi
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* ========== HELP REQUESTS TAB ========== */}
        {activeTab === 'requests' && (
          <>
            {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {['all', 'open', 'in_progress', 'resolved'].map((status) => {
            const isActive = filter === status;
            const config = status === 'all' 
              ? { label: 'Totale', color: 'bg-gray-100 text-gray-800' }
              : STATUS_CONFIG[status];
            
            return (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`p-4 rounded-xl border-2 transition-all ${
                  isActive 
                    ? 'border-umbria-olive bg-umbria-olive/5' 
                    : 'border-transparent bg-white hover:border-umbria-stone'
                }`}
                data-testid={`filter-${status}`}
              >
                <div className="text-2xl font-semibold text-umbria-charcoal">
                  {openCounts[status]}
                </div>
                <div className="text-sm text-gray-500">{config.label}</div>
              </button>
            );
          })}
        </div>

        {/* Help Requests List */}
        <div className="bg-white rounded-2xl shadow-sm border border-umbria-stone/50 overflow-hidden">
          {isLoading ? (
            <div className="p-12 text-center">
              <RefreshCw className="w-8 h-8 animate-spin text-umbria-olive mx-auto mb-3" />
              <p className="text-gray-500">Caricamento...</p>
            </div>
          ) : helpRequests.length === 0 ? (
            <div className="p-12 text-center">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className="text-gray-500">Nessuna richiesta {filter !== 'all' ? `${STATUS_CONFIG[filter]?.label.toLowerCase()}` : ''}</p>
            </div>
          ) : (
            <div className="divide-y divide-umbria-stone/30">
              {helpRequests.map((request) => {
                const statusConfig = STATUS_CONFIG[request.status];
                const priorityConfig = PRIORITY_CONFIG[request.priority];
                const StatusIcon = statusConfig?.icon || Clock;
                
                return (
                  <div 
                    key={request.id}
                    className="p-4 hover:bg-umbria-stone/10 transition-colors cursor-pointer"
                    onClick={() => setSelectedRequest(request)}
                    data-testid={`help-request-${request.id}`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Status icon */}
                      <div className={`p-2 rounded-lg ${statusConfig?.color || 'bg-gray-100'}`}>
                        <StatusIcon size={18} />
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs font-medium ${priorityConfig?.color}`}>
                            P{request.priority}
                          </span>
                          <span className="text-xs bg-umbria-stone/50 px-2 py-0.5 rounded">
                            {CATEGORY_LABELS[request.category] || request.category}
                          </span>
                        </div>
                        
                        <p className="text-sm text-umbria-charcoal font-medium mb-1 truncate">
                          {request.description}
                        </p>
                        
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span>Thread: {request.thread_id?.slice(0, 8)}...</span>
                          <span>{new Date(request.created_at).toLocaleDateString('it-IT')}</span>
                        </div>
                        
                        {request.proposed_alternatives && request.proposed_alternatives.length > 0 && (
                          <div className="mt-2 text-xs text-gray-600">
                            Alternative: {request.proposed_alternatives.join(', ')}
                          </div>
                        )}
                        
                        {request.resolution_notes && (
                          <div className="mt-2 p-2 bg-green-50 rounded text-xs text-green-800">
                            {request.resolution_notes}
                          </div>
                        )}
                      </div>
                      
                      {/* Actions */}
                      <ChevronDown size={18} className="text-gray-400" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
          </>
        )}
      </main>

      {/* Detail/Resolution Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setSelectedRequest(null)}
          />
          
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg animate-slide-up" data-testid="request-modal">
            <div className="p-6">
              <h3 className="font-serif text-xl font-semibold text-umbria-charcoal mb-4">
                Dettaglio Richiesta
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Categoria</label>
                  <p className="text-sm text-umbria-charcoal">
                    {CATEGORY_LABELS[selectedRequest.category] || selectedRequest.category}
                  </p>
                </div>
                
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Descrizione</label>
                  <p className="text-sm text-umbria-charcoal">{selectedRequest.description}</p>
                </div>
                
                {selectedRequest.proposed_alternatives && (
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase">Alternative Proposte</label>
                    <p className="text-sm text-umbria-charcoal">
                      {selectedRequest.proposed_alternatives.join(', ')}
                    </p>
                  </div>
                )}
                
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Thread ID</label>
                  <p className="text-sm text-umbria-charcoal font-mono">{selectedRequest.thread_id}</p>
                </div>
                
                {selectedRequest.status !== 'resolved' && (
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase mb-2 block">
                      Note di Risoluzione
                    </label>
                    <textarea
                      value={resolutionNotes}
                      onChange={(e) => setResolutionNotes(e.target.value)}
                      className="w-full p-3 border border-umbria-stone rounded-xl text-sm focus:ring-2 focus:ring-umbria-olive/20 focus:border-umbria-olive"
                      rows={3}
                      placeholder="Aggiungi note sulla risoluzione..."
                      data-testid="resolution-notes-input"
                    />
                  </div>
                )}
              </div>
              
              <div className="flex gap-3 mt-6">
                {selectedRequest.status === 'open' && (
                  <button
                    onClick={() => updateRequest(selectedRequest.id, { status: 'in_progress' })}
                    disabled={isUpdating}
                    className="btn-secondary flex-1"
                    data-testid="mark-in-progress-btn"
                  >
                    In Lavorazione
                  </button>
                )}
                
                {selectedRequest.status !== 'resolved' && (
                  <button
                    onClick={resolveRequest}
                    disabled={isUpdating}
                    className="btn-primary flex-1"
                    data-testid="resolve-btn"
                  >
                    {isUpdating ? 'Salvataggio...' : 'Risolvi'}
                  </button>
                )}
                
                <button
                  onClick={() => setSelectedRequest(null)}
                  className="btn-ghost"
                  data-testid="close-modal-btn"
                >
                  Chiudi
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* ========== PROVIDER DETAIL MODAL ========== */}
      {selectedProviderId && (
        <ProviderDetail
          providerId={selectedProviderId}
          onClose={() => setSelectedProviderId(null)}
          onUpdate={() => {
            // Refresh data when provider is updated
            loadHelpRequests();
          }}
        />
      )}
    </div>
  );
};

export default AdminPage;
